import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  WidgetConfigurationModalComponent
} from '@components/pages/widgets/widget-configuration-modal/widget-configuration-modal.component';
import {RangeType} from '@constants/pages/user-defined-page/range-type';
import {
  DAY_IN_MINUTES,
  DAY_IN_SECONDS,
  HOUR_IN_MINUTES,
  HOUR_IN_SECONDS,
  MINUTE_IN_SECONDS
} from '@constants/pages/user-defined-page/time-constants';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {MetaData} from '@models/adminstration/meta-data';
import {Machine} from '@models/machine/machine';
import {
  StatusEventPageEventDisplayDetails
} from '@models/pages/status-event-page/status-event-page-event-display-details';
import {
  StatusEventPageSignalDataResponse
} from '@models/pages/status-event-page/status-event-page-signal-data-response';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {MenuItem} from '@models/sidebar/menu-item';
import {MenuItemIcon} from '@models/sidebar/menu-item-icon';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';
import {MachineService} from '@services/administration/machine/machine.service';
import {SettingsService} from '@services/administration/settings/settings.service';
import {GeneralChartDataUpdateService} from '@services/charts/general-chart-data-update.service';
import {GeneralChartSettingsGeneratorService} from '@services/charts/general-chart-settings-generator.service';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {DateService} from '@services/date.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {ModalService} from '@services/modal/modal.service';
import {NotificationService} from '@services/notification/notification.service';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {StatusEventPageDataService} from '@services/pages/status-event-page/data/status-event-page-data.service';
import {UserDefinedPageService} from '@services/pages/user-defined-page/user-defined-page.service';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';
import {RealtimePollingService} from '@services/realtime-polling/realtime-polling.service';
import {cloneDeep} from 'lodash';
import {combineLatest, EMPTY, Observable, Subject, Subscription, timer} from 'rxjs';
import {catchError, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';


@Component({
  selector: 'scitis-widget-item',
  templateUrl: './widget-item.component.html',
  styleUrls: ['./widget-item.component.scss'],
  standalone: false
})
export class WidgetItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  title = '';
  @Input()
  widgetType: UserDefinedWidgetType = UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS;
  @Input()
  chartId: string = '';
  @Input()
  isResized: boolean = false;
  @Input()
  isInEditMode: boolean = false;
  @Input()
  firebaseUserId: string = '';
  @Input()
  machine: Machine = new Machine();
  @Input()
  widgetItem: UserDefinedWidget = new UserDefinedWidget();
  @Input()
  isParentPageVisible: boolean = true;
  @Input()
  forceHighChartsChartReflow: boolean = false;

  @Input()
  menuItem: MenuItem = new MenuItem();

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  removeWidgetEvent = new EventEmitter<[string, string]>();
  @Output()
  moveToInteractiveDataAnalysisEvent: EventEmitter<ChartSignalEntry[]> = new EventEmitter<ChartSignalEntry[]>();

  lineWidgetZoomSubscription: Subscription = new Subscription();
  widgetData: any[] = [];
  isLoading: boolean = true;
  moveToIDA: string = '';
  statusEventDetails: StatusEventPageEventDisplayDetails[] = [];
  editCommandEnabled = false;
  isCommandModeDisabled = true;
  readonly UserDefinedWidgetType = UserDefinedWidgetType;
  private RUNNING_OFFSET = 30 * 1000;
  private SAMPLING_SIZE = 1;
  private pollingInProgress = false;
  private META_DATA_POLLING_KEY: string = '';
  private MACHINE_DATA_POLLING_KEY: string = '';
  private isAlreadyCalled: boolean = false;
  private CURRENT_PAGE_NAME = 'WidgetItemComponent';
  private readonly statusPageWasNotFoundMessage = '';
  private readonly unableToNavigateToStatusPageMessage = '';
  private widgetLastUpdatedDate: Date = new Date();
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private stopMetaDataPolling$: Subject<void> = new Subject<void>();
  private readonly ENABLE_COMMAND_WIDGET = 'ENABLE_COMMAND_WIDGET';

  constructor(private translate: TranslateService,
              private confirmationDialogService: ConfirmationDialogService,
              private modalService: ModalService,
              private userDefinedPageService: UserDefinedPageService,
              private realtimePollingService: RealtimePollingService,
              private generalChartDataUpdateService: GeneralChartDataUpdateService,
              private generalChartSettingsGeneratorService: GeneralChartSettingsGeneratorService,
              private machineService: MachineService,
              private dateService: DateService,
              private menuNotificationService: MenuNotificationService,
              private statusEventService: StatusEventPageDataService,
              private statusEventPageService: StatusEventPageService,
              private leftMenuService: LeftMenuService,
              private settingsService: SettingsService,
              private notificationService: NotificationService,
              private elasticMetaDataService: MetadataElasticSearchService) {
    this.statusPageWasNotFoundMessage = this.translate.instant('Unable to navigate to Status page because it was not found.');
    this.unableToNavigateToStatusPageMessage = this.translate.instant('Unable to navigate to Status page.');
  }

  get isHighchartsChart() {
    return this.widgetType === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS ||
        this.widgetType === UserDefinedWidgetType.BAR ||
        this.widgetType === UserDefinedWidgetType.PIE ||
        this.widgetType === UserDefinedWidgetType.GAUGE;
  }

  get isImage(): boolean {
    return this.widgetType === UserDefinedWidgetType.IMAGE;
  }

  get isChartLine(): boolean {
    return this.widgetType === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS;
  }

  get isMap(): boolean {
    return this.widgetType === UserDefinedWidgetType.MAP;
  }

  get isText(): boolean {
    return this.widgetType === UserDefinedWidgetType.TEXT;
  }

  get isStatus(): boolean {
    return this.widgetType === UserDefinedWidgetType.STATUS;
  }

  get displayDetailsButton(): boolean {
    return (this.isChartLine || this.isStatus) && !this.isInEditMode;
  }

  ngOnInit(): void {
    this.userDefinedPageService.onEditLayout$.subscribe({
      next: _ => {
        this.editCommandEnabled = false;
      }
    });
    this.subscribeToLineWidgetZoom();
    this.settingsService.findPortalSetting(this.ENABLE_COMMAND_WIDGET).subscribe({
      next: result => {
        if (result.value == 'TRUE') {
          this.isCommandModeDisabled = false;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes?.isParentPageVisible && !changes.isParentPageVisible.currentValue) {
      this.stopDataRetrieval();
    }

    if (changes?.isInEditMode?.currentValue) {
      this.stopDataRetrieval();
    }

    if (changes && (changes.chartId || changes.machine || changes.firebaseUserId ||
        (changes?.forceHighChartsChartReflow?.currentValue) ||
        (changes?.isParentPageVisible?.currentValue) ||
        (changes.isInEditMode && !changes.isInEditMode.currentValue))) {
      this.getWidgetData();
    }
  }

  ngOnDestroy(): void {
    this.stopDataRetrieval();
    this.lineWidgetZoomSubscription.unsubscribe();
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  onDeleteWidget() {
    const removeMessageTitle = this.translate.instant('Delete widget');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete the widget %s ?').replace('%s', this.title.toBold());
    const removeMessageOK = this.translate.instant('Delete');

    this.confirmationDialogService.handleConfirmationDialog(removeMessageTitle, removeMessageBody, '',
        removeMessageOK)
    .pipe(takeUntil(this.$destroy))
    .subscribe(userConfirmed => {
      if (userConfirmed) {
        this.removeWidgetEvent.emit([this.chartId, this.widgetItem.widgetDetails.widgetName]);
      }
    });
  }

  public onEditWidget() {
    if (this.isMap && !this.machine.isLocationAvailable()) {
      return;
    }
    const widgetBackup = cloneDeep(this.widgetItem);
    const modalRef = this.modalService.open(WidgetConfigurationModalComponent, {size: 'lg'});
    modalRef.componentInstance.isAddOperation = false;
    modalRef.componentInstance.userDefinedWidget = this.widgetItem;
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.isTemplateWidget = this.isTemplateWidget;

    modalRef.result.then((result: UserDefinedWidget) => {
      this.widgetItem = result;
    }, () => {
      this.widgetItem = widgetBackup;
    });

  }

  getWidgetData() {
    this.stopDataRetrieval();
    if (this.isImage || this.isMap) {
      this.isLoading = false;
      return;
    }

    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: result => {
        if (result && this.shouldStartPolling()) {
          this.determineSamplingSize();
          if (!this.widgetItem?.userDefinedChartDetails.usingStaticData) {
            this.loadRealtimeData();
          } else {
            this.loadStaticData();
          }
        } else {
          this.isLoading = false;
        }
      }
    });
  }

  setCommandMode(event: any) {
    this.editCommandEnabled = event.target?.checked;
    if (this.editCommandEnabled) {
      this.stopDataRetrieval();
    } else {
      this.startDataRetrieval();
    }
  }

  getTimeTooltip() {
    const hours = String(this.widgetLastUpdatedDate.getHours()).padStart(2, '0');
    const minutes = String(this.widgetLastUpdatedDate.getMinutes()).padStart(2, '0');
    const seconds = String(this.widgetLastUpdatedDate.getSeconds()).padStart(2, '0');
    return this.translate.instant('Last Update: ') + hours + ':' + minutes + ':' + seconds;
  }

  triggerMoveToInteractiveDataAnalysis() {
    if (!this.widgetItem) {
      return;
    }
    if (this.isStatus) {
      this.redirectToStatusPage();
    } else {
      console.log('Moving to IDA');
      this.moveToIDA = 'move_' + new Date().getTime();
    }
  }

  moveToInteractiveDataAnalysis($event: ChartSignalEntry[]) {
    this.moveToInteractiveDataAnalysisEvent.emit($event);
  }

  private filterAndSortEvents(statusEventPageConfiguration: StatusEventPage, data: Map<string, StatusEventPageSignalDataResponse[]>): StatusEventPageEventDisplayDetails[] {
    return this.statusEventService.parseStatusEventResponseToDisplayWidgetDetails(statusEventPageConfiguration, data);
  }

  private loadRealtimeData() {
    if (this.isStatus) {
      this.startPollingStatusPage();
    } else {
      this.startDataRetrieval();
    }
  }

  private loadStatusEventPageConfiguration(machineId: string, pageId: number): Observable<StatusEventPage> {
    return this.statusEventPageService.getStatusEventPage(machineId, String(pageId));
  }

  private shouldStartPolling(): boolean {
    if (!this.machine?.id) {
      return false;
    }

    if (!this.firebaseUserId || !this.chartId) {
      return false;
    }

    return !(this.pollingInProgress || !this.isParentPageVisible || this.isInEditMode);
  }

  private startDataRetrieval() {
    if (!this.widgetItem) {
      return;
    }
    this.widgetItem.userDefinedChartDetails = {...this.widgetItem.userDefinedChartDetails};
    this.isLoading = true;
    this.pollingInProgress = true;
    const usesTimeRangeWindow: boolean = this.widgetType === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS ||
        this.widgetType === UserDefinedWidgetType.PIE;

    const timestamp = this.createTimestampFromUDPDetails(usesTimeRangeWindow);
    const realtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: Number(this.machine.id),
      organizationId: 1,
      refresh: 10,
      firebaseUserId: this.firebaseUserId,
      chartId: +this.chartId,
      timestamp,
      initial: true,
      sampling: this.SAMPLING_SIZE,
      templateWidget: this.isTemplateWidget
    });

    this.realtimePollingService.start(this.userDefinedPageService.GET_WIDGET_DATA_URL_SUFFIX, realtimePollingRequestParameters, this.chartId)
    .pipe(takeUntil(this.$destroy))
    .subscribe((response) => {
      if (!response) {
        this.isLoading = false;
        this.widgetData = [];
      } else {
        this.isLoading = false;
        this.widgetLastUpdatedDate = new Date();
        this.widgetData = this.generalChartDataUpdateService.prepareDataToDisplay(this.widgetType, response);
      }
    });
  }

  private startPollingStatusPage() {
    if (!this.widgetItem) {
      return;
    }
    const pageId = this.widgetItem.userDefinedChartDetails.statusEventPageId;
    if (!pageId) {
      this.isLoading = false;
      this.pollingInProgress = false;
      return;
    }
    this.isLoading = true;
    this.pollingInProgress = true;
    const timestamp = this.createTimestampFromUDPDetails(this.widgetType === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS);
    const realtimePollingRequestParameters = new RealtimePollingRequestParameters({
      refresh: this.widgetItem.userDefinedChartDetails.rangeValue,
      machineId: Number(this.machine.id),
      organizationId: 1,
      firebaseUserId: this.firebaseUserId,
      chartId: +this.chartId,
      initial: true,
      sampling: this.SAMPLING_SIZE,
      timestamp: timestamp,
      pageId: pageId,
      templateWidget: this.isTemplateWidget
    });

    const configuration$ = this.loadStatusEventPageConfiguration(String(this.machine.id), pageId).pipe(
        catchError(error => {
          console.log('There was an error loading status page configuration', error);
          this.isLoading = false;
          this.pollingInProgress = false;
          return EMPTY;
        }),
        shareReplay({bufferSize: 1, refCount: true})
    );

    combineLatest([
      configuration$,
      this.statusEventService.getStatusWidgetInitialData(realtimePollingRequestParameters)
    ])
    .pipe(takeUntil(this.$destroy))
    .subscribe(([configuration, response]: [StatusEventPage, Map<string, StatusEventPageSignalDataResponse[]>]) => {
      this.widgetLastUpdatedDate = new Date();
      if (response) {
        this.statusEventDetails = this.filterAndSortEvents(configuration, response);
      }
      this.isLoading = false;
    });


    combineLatest([
      configuration$,
      this.realtimePollingService.start(this.statusEventService.GET_STATUS_EVENT_WIDGET_DATA_URL_SUFFIX, realtimePollingRequestParameters, this.chartId)
    ])
    .pipe(takeUntil(this.$destroy))
    .subscribe(([configuration, response]: [StatusEventPage, Map<string, StatusEventPageSignalDataResponse[]>[]]) => {
      this.widgetLastUpdatedDate = new Date();
      if (response && response.length > 0) {
        this.statusEventDetails = this.filterAndSortEvents(configuration, response[0]);
      }
      this.isLoading = false;
    });
  }

  private loadStaticData() {
    const properties = this.widgetItem?.userDefinedChartDetails.staticData;
    if (!properties) {
      return;
    }
    if (this.isAlreadyCalled) {
      return;
    }
    this.isAlreadyCalled = true;
    this.isLoading = true;
    this.widgetData = [];
    this.pollStaticMachineData(properties);
  }

  private pollStaticMachineData(properties: string[]) {
    if (!this.widgetItem) {
      return;
    }
    if (this.widgetItem.userDefinedChartDetails.rangeValue > 0) {
      this.getMachineDataWithPolling(properties);
    } else {
      this.getMachineDataWithoutPolling(properties);
    }
  }

  private getMachineDataWithPolling(properties: string[]) {
    if (!this.machine.id) {
      return;
    }

    this.isLoading = true;
    this.pollingInProgress = true;
    this.MACHINE_DATA_POLLING_KEY = this.chartId + '_MACHINE';
    const realtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: this.machine.id,
      organizationId: 1,
      firebaseUserId: this.firebaseUserId,
      chartId: +this.chartId,
      refresh: this.widgetItem?.userDefinedChartDetails.rangeValue,
    });
    this.realtimePollingService.start(this.machineService.MACHINE_DETAILS_URL_SUFFIX, realtimePollingRequestParameters, this.MACHINE_DATA_POLLING_KEY)
    .pipe(takeUntil(this.$destroy))
    .subscribe((response) => {
      this.widgetLastUpdatedDate = new Date();
      if (!response || response.length === 0) {
        this.getMachineMetaDataWithPolling(properties);
      } else {
        const machineData = response[0];
        this.setMachineDataWidgetData(machineData, properties);
        this.getMachineMetaDataWithPolling(properties);
      }
    });
  }

  private getMachineDataWithoutPolling(properties: string[]) {
    if (!this.machine.id) {
      return;
    }
    this.machineService.getMachineConfiguration(this.machine.id)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (result) => {
        this.setMachineDataWidgetData(result, properties);
        this.getMachineMetaDataWithoutPolling(properties);
      }, error: (error) => {
        console.log(error);
        this.getMachineMetaDataWithoutPolling(properties);
      }
    });
  }

  private setMachineDataWidgetData(machineData, properties: string[]) {
    const machineDataMap: Map<string, string> = new Map<string, string>();
    for (const prop of properties) {
      if (prop in machineData) {
        const value = this.getValue(prop, machineData);
        machineDataMap.set(prop, value);
      }
    }
    this.widgetData.push(machineDataMap);
  }

  private getValue(property: string, machineData) {
    let val = machineData[property];
    if (property.toLowerCase() === 'creationtime') {
      val = this.formatDate(machineData[property]);
    }
    return val;
  }

  private formatDate(date: number): string {
    const formattedDate = this.dateService.getMediumDate(date);
    if (!formattedDate) {
      return '';
    }
    return formattedDate;
  }

  private getMachineMetaDataWithoutPolling(properties: string[]) {
    if (!this.machine?.id) {
      return;
    }
    this.elasticMetaDataService.listAllMachineMetaData(this.machine.id)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (elasticResponse: ElasticSearchResponseEntity) => {
        const machineMetaData = elasticResponse?.result?.data;
        this.setMetaDataWidgetData(machineMetaData, properties);
        this.isLoading = false;
        this.isAlreadyCalled = false;
      }, error: (_error) => {
        this.isLoading = false;
        this.isAlreadyCalled = false;
      }
    });
  }

  private setMetaDataWidgetData(metaData: MetaData[], properties: string[]) {
    const machineDataMap: Map<string, string> = new Map<string, string>();
    for (const field of metaData) {
      for (const prop of properties) {
        if (field.key?.toLowerCase() === prop.toLowerCase()) {
          const value = this.getMetaDataValue(field);
          machineDataMap.set(prop, value);
        }
      }
    }
    this.widgetData.push(machineDataMap);
  }

  private getMetaDataValue(field: MetaData): string {
    if (!field.value) {
      return '';
    }

    return field.value;
  }

  private getMachineMetaDataWithPolling(properties: string[]) {
    if (!this.machine.id) {
      return;
    }
    this.stopMetaDataPolling$.next();
    this.isLoading = true;
    this.pollingInProgress = true;
    const refresh = this.widgetItem?.userDefinedChartDetails.rangeValue;
    const refreshIntervalSeconds = refresh > 0 ? refresh : 10;
    timer(0, refreshIntervalSeconds * 1000)
    .pipe(
        switchMap(() => this.elasticMetaDataService.listAllMachineMetaData(this.machine.id ?? null)),
        takeUntil(this.stopMetaDataPolling$),
        takeUntil(this.$destroy)
    )
    .subscribe({
      next: (elasticResponse: ElasticSearchResponseEntity) => {
        this.widgetLastUpdatedDate = new Date();
        const machineMetaData = elasticResponse?.result?.data;
        if (!machineMetaData || machineMetaData.length === 0) {
          this.isLoading = false;
          this.isAlreadyCalled = false;
        } else {
          this.setMetaDataWidgetData(machineMetaData, properties);
          this.isLoading = false;
          this.isAlreadyCalled = false;
        }
      }, error: (_error) => {
        this.isLoading = false;
        this.isAlreadyCalled = false;
      }
    });
  }

  private stopDataRetrieval() {
    this.pollingInProgress = false;
    this.stopMetaDataPolling$.next();
    this.realtimePollingService.stop(this.chartId);
    this.realtimePollingService.stop(this.MACHINE_DATA_POLLING_KEY);
    this.realtimePollingService.stop(this.META_DATA_POLLING_KEY);
  }

  private redirectToStatusPage(): void {
    if (!this.machine.id && !this.menuItem.menuItemParent) {
      this.notificationService.error(this.unableToNavigateToStatusPageMessage, null, this.CURRENT_PAGE_NAME, true);
      return;
    }

    const isMachineGroup = this.menuItem.menuItemParent!.isMachineGroup;
    this.leftMenuService.getMenuItems(String(this.machine.id), isMachineGroup)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (menuItems: MenuItem[]) => {
        let statusPageFound = false;
        menuItems.forEach(menuItem => {
          if (menuItem.pageId === String(this.widgetItem?.userDefinedChartDetails.statusEventPageId) ||
              menuItem.name.toLowerCase() == this.widgetItem?.userDefinedChartDetails.statusEventPageName?.toLowerCase()
          ) {
            statusPageFound = true;
            const statusEventPageMenuItem: MenuItem = new MenuItem();
            statusEventPageMenuItem.pageId = String(this.widgetItem?.userDefinedChartDetails.statusEventPageId);
            statusEventPageMenuItem.icon = MenuItemIcon.STATUS_EVENT_PAGE;
            statusEventPageMenuItem.menuItemParent = this.menuItem.menuItemParent;
            statusEventPageMenuItem.machineId = String(this.machine.id);
            statusEventPageMenuItem.organizationId = this.machine.parentId;
            statusEventPageMenuItem.type = MenuItemType.STATUS_EVENT_PAGE;
            statusEventPageMenuItem.machineCategory = this.machine.category;
            statusEventPageMenuItem.id = menuItem.id;
            statusEventPageMenuItem.name = menuItem.name;
            this.menuNotificationService.broadcastClickOnDashboard(statusEventPageMenuItem);
          }
        });
        if (!statusPageFound) {
          this.notificationService.error(this.statusPageWasNotFoundMessage, null, this.CURRENT_PAGE_NAME, true);
        }
      }, error: error => {
        this.notificationService.error(this.unableToNavigateToStatusPageMessage, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  private createTimestampFromUDPDetails(lineGraph: boolean): number {
    let minutes: number = 1;
    switch (this.widgetItem?.userDefinedChartDetails.rangeType) {
      case RangeType.DAY:
        minutes = DAY_IN_MINUTES;
        break;
      case RangeType.HOUR:
        minutes = HOUR_IN_MINUTES;
        break;
    }

    const rangeValueMinutes: number = lineGraph ? this.widgetItem?.userDefinedChartDetails.rangeValue * minutes : 0;
    const rangeValueMilliseconds: number = (rangeValueMinutes ? rangeValueMinutes * 60 * 1000 : this.RUNNING_OFFSET);
    return new Date().getTime() - (this.RUNNING_OFFSET + rangeValueMilliseconds);
  }

  private determineSamplingSize() {
    let rangeValueInSeconds = this.widgetItem.userDefinedChartDetails.rangeValue * 60;
    switch (this.widgetItem.userDefinedChartDetails.rangeType) {
      case RangeType.HOUR:
        rangeValueInSeconds *= HOUR_IN_MINUTES;
        break;
      case RangeType.DAY:
        rangeValueInSeconds *= DAY_IN_MINUTES;
        break;
    }

    this.SAMPLING_SIZE = this.calculateTimeAggregation(rangeValueInSeconds);
  }

  private calculateTimeAggregation(valueInSeconds: number): number {
    let samplingSize: number;

    switch (true) {
      case valueInSeconds <= MINUTE_IN_SECONDS * 5:
        samplingSize = 1;
        break;
      case valueInSeconds <= MINUTE_IN_SECONDS * 30:
        samplingSize = 5;
        break;
      case valueInSeconds <= HOUR_IN_SECONDS * 2:
        samplingSize = 10;
        break;
      case valueInSeconds <= HOUR_IN_SECONDS * 12:
        samplingSize = MINUTE_IN_SECONDS;
        break;
      case valueInSeconds <= DAY_IN_SECONDS:
        samplingSize = MINUTE_IN_SECONDS * 5;
        break;
      case valueInSeconds <= DAY_IN_SECONDS * 7:
        samplingSize = MINUTE_IN_SECONDS * 30;
        break;
      default:
        samplingSize = HOUR_IN_SECONDS;
        break;
    }

    return samplingSize;
  }

  private subscribeToLineWidgetZoom() {
    this.lineWidgetZoomSubscription = this.generalChartSettingsGeneratorService.getZoomEventObservable(this.widgetType)
    .pipe(takeUntil(this.$destroy))
    .subscribe((zoomedIn: boolean) => {
      if (this.isInEditMode) {
        return;
      }
      if (this.widgetType !== UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS) {
        return;
      }

      if (zoomedIn) {
        this.stopDataRetrieval();
      } else {
        this.getWidgetData();
      }
    });
  }
}
